import { zodResolver } from '@hookform/resolvers/zod';
import { Link, useNavigate } from '@tanstack/react-router';
import { ArrowLeft, FilePlus2 } from 'lucide-react';
import type { ReactNode } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { createDirectJobSchema, type CreateDirectJobInput } from '@nest-recruit/shared';
import { Button } from '../../../components/Button';
import { Card } from '../../../components/Card';
import { Field } from '../../../components/Field';
import { Input } from '../../../components/Input';
import { Select } from '../../../components/Select';
import { ForbiddenState } from '../../../components/system-states/SystemState';
import { errorMessage } from '../../../lib/api-client';
import { useCanEdit } from '../../../stores/session';
import { useToasts } from '../../../stores/toasts';
import { useDepartments, useVentures } from '../../organisation/api/organisation-api';
import { useCreateDirectJob } from '../api/jobs-api';
import styles from './JobStartPage.module.css';

const MAX_HEADCOUNT = 50;

/**
 * F5.24 (D20, brought into R1 on 2026-10-08) — a job HR creates directly, with no hire request
 * behind it. Only what a requisition would have locked is asked here; everything else is the
 * setup's, from step 1, where a template or last month's job can be copied in one click.
 */
export function NewJobPage(): ReactNode {
  const canCreate = useCanEdit('job_config');
  const ventures = useVentures();
  const departments = useDepartments();
  const create = useCreateDirectJob();
  const navigate = useNavigate();
  const show = useToasts((state) => state.show);
  const form = useForm<CreateDirectJobInput>({
    resolver: zodResolver(createDirectJobSchema),
    mode: 'onBlur',
    defaultValues: { title: '', ventureId: '', departmentId: '', slotsTotal: 1 },
  });
  const ventureId = useWatch({ control: form.control, name: 'ventureId' });

  if (!canCreate) return <ForbiddenState resource="job setup" contact="Your admin" />;

  const ventureOptions = (ventures.data ?? []).filter((venture) => venture.archivedAt === null).map((venture) => ({ value: venture.id, label: venture.name }));
  const departmentOptions = (departments.data ?? [])
    .filter((department) => department.archivedAt === null && department.ventureId === ventureId)
    .map((department) => ({ value: department.id, label: department.name }));

  const submit = form.handleSubmit(async (input) => {
    try {
      const created = await create.mutateAsync(input);
      show({ tone: 'success', message: 'Job created', detail: 'Set it up next: a template or a previous job can fill most of it.' });
      void navigate({ to: '/jobs/$jobId/setup/$section', params: { jobId: created.id, section: 'basics' } });
    } catch (cause) {
      show({ tone: 'danger', message: 'The job was not created', detail: errorMessage(cause) });
    }
  });

  return (
    <div className={styles.page}>
      <Link to="/jobs" className={styles.back}>
        <ArrowLeft size={14} aria-hidden />
        Jobs
      </Link>
      <h1 className={styles.title}>New job</h1>

      <Card title="The job" meta="Everything else is set up on the next screen">
        <form className={styles.form} onSubmit={(event) => void submit(event)} noValidate>
          <Field label="Job title" isRequired error={form.formState.errors.title?.message}>
            {({ inputId, describedBy, isInvalid }) => (
              <Input id={inputId} aria-describedby={describedBy} isInvalid={isInvalid} autoFocus {...form.register('title')} />
            )}
          </Field>
          <div className={styles.pair}>
            <Field label="Venture" isRequired error={form.formState.errors.ventureId?.message}>
              {({ inputId, describedBy, isInvalid }) => (
                <Controller
                  control={form.control}
                  name="ventureId"
                  render={({ field }) => (
                    <Select
                      id={inputId}
                      describedBy={describedBy}
                      isInvalid={isInvalid}
                      label="Venture"
                      options={ventureOptions}
                      value={field.value === '' ? null : field.value}
                      onChange={(value) => {
                        field.onChange(value);
                        form.setValue('departmentId', '');
                      }}
                    />
                  )}
                />
              )}
            </Field>
            <Field label="Department" isRequired error={form.formState.errors.departmentId?.message}>
              {({ inputId, describedBy, isInvalid }) => (
                <Controller
                  control={form.control}
                  name="departmentId"
                  render={({ field }) => (
                    <Select
                      id={inputId}
                      describedBy={describedBy}
                      isInvalid={isInvalid}
                      label="Department"
                      placeholder={ventureId === '' ? 'Choose the venture first' : 'Select'}
                      disabled={ventureId === ''}
                      options={departmentOptions}
                      value={field.value === '' ? null : field.value}
                      onChange={field.onChange}
                    />
                  )}
                />
              )}
            </Field>
          </div>
          <div className={styles.narrow}>
            <Field label="People needed" isRequired hint={`1 to ${MAX_HEADCOUNT}`} error={form.formState.errors.slotsTotal?.message}>
              {({ inputId, describedBy, isInvalid }) => (
                <Input
                  id={inputId}
                  aria-describedby={describedBy}
                  isInvalid={isInvalid}
                  isNumeric
                  type="number"
                  inputMode="numeric"
                  min={1}
                  max={MAX_HEADCOUNT}
                  {...form.register('slotsTotal', { valueAsNumber: true })}
                />
              )}
            </Field>
          </div>
          <div className={styles.actions}>
            <Button type="submit" variant="solid" icon={FilePlus2} isPending={create.isPending}>
              Create the job
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}
